import { useState } from "react";

import { useGetCodeDescriptionApiCodesSystemCodeGet } from "@/codegen/openapi/client";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export type CodeSystem =
  | "icd10"
  | "cpt"
  | "hcpcs"
  | "modifier"
  | "carc"
  | "rarc"
  | "group";

const VARIANT: Record<CodeSystem, string> = {
  icd10: "border-green-200 bg-green-50 text-green-800",
  cpt: "border-sky-200 bg-sky-50 text-sky-800",
  hcpcs: "border-sky-200 bg-sky-50 text-sky-800",
  modifier: "border-violet-200 bg-violet-50 text-violet-800",
  carc: "border-rose-200 bg-rose-50 text-rose-800",
  rarc: "border-amber-200 bg-amber-50 text-amber-800",
  group: "border-slate-200 bg-slate-50 text-slate-700",
};

const BASE =
  "inline-flex items-center rounded-md border px-1.5 py-0.5 text-[11px] font-mono cursor-help";

/**
 * Code pill with a hover tooltip that fetches the description from
 * /api/codes/{system}/{code}. React Query caches per (system, code) so
 * repeat hovers don't re-fetch. The fetch is deferred until the
 * tooltip first opens.
 */
export function CodePill({ system, code }: { system: CodeSystem; code: string }) {
  const [hasOpened, setHasOpened] = useState(false);
  const { data, isPending, isError } = useGetCodeDescriptionApiCodesSystemCodeGet(
    system,
    code,
    {
      query: {
        enabled: hasOpened,
        staleTime: Infinity,
        retry: false,
      },
    },
  );

  const description = data?.data?.description;

  return (
    <Tooltip onOpenChange={(open) => open && setHasOpened(true)}>
      <TooltipTrigger asChild>
        <span className={`${BASE} ${VARIANT[system]}`}>{code}</span>
      </TooltipTrigger>
      <TooltipContent>
        {description ? (
          description
        ) : isPending && hasOpened ? (
          "Loading…"
        ) : isError ? (
          "No description available"
        ) : (
          "—"
        )}
      </TooltipContent>
    </Tooltip>
  );
}
